import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { EnfafIconComponent } from '../enfaf-icon/enfaf-icon.component';
import { ToastService } from '../../core/services/toast/toast.service';
import { UtilsService } from '../../shared/utils/utils.service';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-enfaf-img-dragger',
    imports: [EnfafIconComponent, CommonModule],
    templateUrl: './enfaf-img-dragger.component.html',
    styleUrl: './enfaf-img-dragger.component.scss'
})
export class EnfafImgDraggerComponent {
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Input() urlImage: string = '';
  @Input() maxFileSize: number = 5 * 1024 * 1024;
  @Input() title: string = '';
  @Input() actionText: string = 'Subir imagen';
  @Input() minHeight?: string;
  @Input() minWidth?: string;
  @Input() width?: string;
  @Input() objectFit: 'cover' | 'contain' = 'cover';

  @Output() onImageUpload = new EventEmitter<{ imageToUpload: File | null; imageToUploadSrc: string | null }>();
  @Output() onImageDelete = new EventEmitter<void>();

  isUploadingImage = false;
  isDraggingOver = false;
  imageToUpload: File | null = null;
  imageToUploadSrc: string | null = null;

  constructor(
    private toast: ToastService,
    private utils: UtilsService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  // Drag & Drop methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDraggingOver = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      if (this.isValidImageFile(file)) {
        this.uploadImage(file);
      }
    }
  }

  // File input methods
  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      if (this.isValidImageFile(file)) {
        this.uploadImage(file);
      }
      input.value = '';
    }
  }

  isValidImageFile(file: File): boolean {
    const acceptedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/avif'];
    if (!acceptedImageTypes.includes(file.type)) {
      this.toast.presentErrorToast('El archivo debe ser una imagen (jpeg, jpg, png, gif, webp, avif)');
      return false;
    }

    if (file.size > this.maxFileSize) {
      const profileImgInput = document.getElementById('profileImg') as HTMLInputElement | null;
      if (profileImgInput) {
        profileImgInput.value = '';
      }
      const fileName = file.name;
      const maxMB = (this.maxFileSize / (1024 * 1024)).toFixed(2);
      const errorMessage = `La imagen '${fileName}' no puede exceder los ${maxMB} MB`;
      this.toast.presentErrorToast(errorMessage);
      return false;
    }
    if (file.size === 0) {
      this.toast.presentErrorToast('El archivo no puede estar vacío');
      return false;
    }
    return true;
  }

  async uploadImage(file: File): Promise<void> {
    try {
      this.isUploadingImage = true;

      this.imageToUploadSrc = await this.utils.previewImageFile(file);
      if (!this.imageToUploadSrc) {
        return;
      }

      this.imageToUpload = file;
      this.changeDetectorRef.detectChanges();
      this.onImageUpload.emit({ imageToUpload: this.imageToUpload, imageToUploadSrc: this.imageToUploadSrc });
      this.isUploadingImage = false;
      this.isDraggingOver = false;
      this.fileInput.nativeElement.value = '';
    } catch (error) {
      this.isUploadingImage = false;
      this.toast.presentErrorToast('Hubo un error adjuntando imagen');
      console.error('Error uploading image:', error);
    }
  }

  deleteImage($event: MouseEvent) {
    $event.stopPropagation();
    $event.preventDefault();
    this.fileInput.nativeElement.value = '';
    this.isUploadingImage = false;
    this.isDraggingOver = false;
    this.imageToUpload = null;
    this.imageToUploadSrc = null;
    this.changeDetectorRef.detectChanges();
    this.onImageDelete.emit();
  }
}
